import { shallowEqual, useDispatch } from "react-redux";
import { Role } from "../../../system/roles/constants";
import { globalConfigState } from "../../ActiveBrand/constants";
import updateActiveBrand from "../../ActiveBrand/api/action";
import { setCookie } from "../../../system/helpers/cookies";
import { APP_ROUTES } from "../../../system/router/constants";
import { getBrandCode } from "../../../system/helpers/helpfulFunctions";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { resetStorage, verifySSOToken } from "./helpers";
import { ApplicationState } from "../../../system/reducers";
import { useSelector } from "react-redux";
import setSSOToken from "./actions";
import { LS_KEYS } from "../../../system/constants/localStorage";
import config from "../../../system/config";

export const useHandleLoginSuccess = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onSuccess = (data, redirect = true, withSSO = false) => {
    if (!data?.profile || (data?.success !== undefined && !data?.success)) {
      return withSSO
        ? window.location.assign(config.SSO_DOMAIN_URL)
        : navigate(APP_ROUTES.login);
    }

    setCookie("token", data.token);

    const isSuperAdmin = Role.SuperAdmin === data.profile.name;
    const hasGlobalConfigAccess = !!data.profile.permissions?.filter(
      (perm: any) => perm.alias.toLowerCase().includes("system"),
    ).length;

    const brand = data.profile.brands?.[0];

    if (!(isSuperAdmin || hasGlobalConfigAccess) && brand) {
      dispatch(
        updateActiveBrand({
          name: brand.name,
          icon: getBrandCode(brand.name),
          color: brand.settings?.["color"],
          id: brand.id,
        }),
      );
    } else {
      dispatch(updateActiveBrand(globalConfigState));
    }
    localStorage.setItem(
      "authData",
      JSON.stringify({
        name: data.profile.name,
        permissions: data.profile.permissions,
      }),
    );

    if (redirect) {
      navigate(APP_ROUTES.dashboard);
    }
  };

  return onSuccess;
};

export const useHandleOAuthLoginSuccess = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleHUBSuccessLogin = useHandleLoginSuccess();

  const onSuccess = (data) => {
    let redirectLocation = localStorage.getItem(LS_KEYS.LOCATION);

    if (!redirectLocation || redirectLocation === APP_ROUTES.oauth) {
      redirectLocation = APP_ROUTES.dashboard;
    }

    localStorage.setItem(LS_KEYS.TOKEN, data?.internal?.access_token);
    localStorage.setItem(
      LS_KEYS.TOKEN_EXPIRATION,
      String(Date.now() + (data?.internal?.expires_in ?? 0) * 1000),
    );
    localStorage.removeItem(LS_KEYS.CODE_VERIFIER);
    localStorage.removeItem(LS_KEYS.LOCATION);

    dispatch(setSSOToken(data?.internal?.access_token));
    handleHUBSuccessLogin(data?.external, true, true);
    navigate(redirectLocation);
  };
  return onSuccess;
};

const isSSOTokenExpired = () => {
  const expiration = Number(localStorage.getItem(LS_KEYS.TOKEN_EXPIRATION));
  return !expiration || Date.now() >= expiration;
};

export const useInitializeToken = () => {
  const dispatch = useDispatch();
  const { sso_token } = useSelector(
    (state: ApplicationState) => state.auth,
    shallowEqual,
  );

  const onLocalStorageChange = (lsEvent) => {
    if (lsEvent.key === LS_KEYS.TOKEN) {
      if (!lsEvent.newValue) {
        resetStorage();
      }
      dispatch(setSSOToken(lsEvent.newValue));
    }
  };

  useEffect(() => {
    const lsToken = localStorage.getItem(LS_KEYS.TOKEN);
    if (lsToken && !sso_token && !isSSOTokenExpired()) {
      dispatch(setSSOToken(lsToken));
    }

    window.addEventListener("storage", onLocalStorageChange);

    return () => window.removeEventListener("storage", onLocalStorageChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const lsToken = localStorage.getItem(LS_KEYS.TOKEN);
    const tokenExpired = lsToken && isSSOTokenExpired();

    if (tokenExpired) {
      resetStorage();
      dispatch(setSSOToken(null));
    }

    if (!sso_token && (!lsToken || tokenExpired)) {
      // SSO token is missing or expired - re-authenticate even when the
      // HUB session is still valid, otherwise sso_token stays null forever
      // and the SSO widget silently never loads (HUB-2402).
      verifySSOToken(window.location);
    }
  }, [sso_token, dispatch]);
};
